import React, { useState, useEffect } from "react";
import { Column, Text } from "@amzn/meridian";
import StepRenderer from "../StepRenderer";
import { useAppDispatch } from "src/store/store";
import { setCurrentIterateStep, setIterationProgress } from "src/store/workflowSlice";
import { setScannedBarcode } from "src/store/inspectionCaptureSlice";
import { CameraStreamProps } from "../types/cameraStreamProps";

interface IterateStepProps {
  iterateSteps: any[];
  drumCount: number | null;
  onIterateComplete: () => void;
  cameraStreamProps: CameraStreamProps;
}

const IterateStep: React.FC<IterateStepProps> = ({
  iterateSteps,
  drumCount,
  onIterateComplete,
  cameraStreamProps,
}) => {
  const dispatch = useAppDispatch();
  const [currentIteration, setCurrentIteration] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = iterateSteps[currentStepIndex];

  // Update Redux with the current iterate step whenever it changes
  useEffect(() => {
    if (drumCount === null || drumCount === 0) return;
    dispatch(setCurrentIterateStep(currentStep));
  }, [currentStepIndex, dispatch, currentStep, drumCount]);

  // Update Redux with iteration progress
  useEffect(() => {
    if (drumCount === null || drumCount === 0) return;
    dispatch(setIterationProgress({
      currentIndex: currentIteration,
      total: drumCount
    }));
  }, [currentIteration, drumCount, dispatch]);

  // If drum count is not set yet, we can't iterate
  if (drumCount === null || drumCount === 0) {
    return (
      <Column alignmentVertical="center" alignmentHorizontal="center">
        <Text type="h200">Waiting for container count...</Text>
      </Column>
    );
  }

  const handleStepComplete = () => {
    // Move to next step in the iteration
    // dispatching the current iterate step lets the WorkflowExecutor 
    // decide whether to keep the camera running or not.
    if (currentStepIndex < iterateSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      dispatch(setCurrentIterateStep(iterateSteps[nextIndex]));
      setCurrentStepIndex(nextIndex);
    } else {
      // Completed all steps in this iteration
      if (currentIteration < drumCount - 1) {
        // Move to next iteration
        dispatch(setCurrentIterateStep(iterateSteps[0]));
        setCurrentIteration(currentIteration + 1);
        setCurrentStepIndex(0);
      } else {
        // Completed all iterations
        dispatch(setCurrentIterateStep(null));
        dispatch(setScannedBarcode(null));
        onIterateComplete();
      }
    }
  };

  return (
    <Column>
      <StepRenderer
        step={currentStep}
        drumCount={drumCount}
        onStepComplete={handleStepComplete}
        cameraStreamProps={cameraStreamProps}
      />
    </Column>
  );
};

export default IterateStep;
